import { v4 as uuid } from 'uuid'

export enum NotificationTypes {
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export enum NotificationScopes {
  BOARD_CREATION = 'BOARD_CREATION',
  FILE_UPLOAD = 'FILE_UPLOAD',
  UPDATE_SAVING_AGGREGATE = 'UPDATE_SAVING_AGGREGATE',
  UPDATE_SAVING = 'UPDATE_SAVING',
  COMPOSER_NOTICE_NOT_PREFILLED = 'COMPOSER_NOTICE_NOT_PREFILLED',
  MC_OMNIBOX_EDIT_NOTICE = 'MC_OMNIBOX_EDIT_NOTICE',
  MC_ROLLOUT_INFO = 'MC_ROLLOUT_INFO',
  TWITTER_DUPLICATE_CONTENT_WARNING = 'TWITTER_DUPLICATE_CONTENT_WARNING',
  COMPOSER_FACEBOOK_AUTOCOMPLETE_DISABLED = 'COMPOSER_FACEBOOK_AUTOCOMPLETE_DISABLED',
  PROFILE_QUEUE_LIMIT = 'PROFILE_QUEUE_LIMIT',
  FB_IG_URL_NO_LINK_PREVIEW = 'FB_IG_URL_NO_LINK_PREVIEW',
  LINK_ATTACHMENT_FAILURE = 'LINK_ATTACHMENT_FAILURE',
  LINK_ATTACHMENT_FAILURE_NO_EDIT = 'LINK_ATTACHMENT_FAILURE_NO_EDIT',
  MEDIA_TRUNCATED = 'MEDIA_TRUNCATED',
}

export interface ErrorNotification {
  type: NotificationTypes.ERROR
  id: string
  scope: NotificationScopes
  errorCode?: number
  message: string
  onlyCloseOnce?: boolean
  showSoftAndHardCloseOptions?: boolean
  data?: Record<string, string>
}

export interface InfoNotification {
  type: typeof NotificationTypes.INFO
  id: string
  scope: `${NotificationScopes}-${string}` | NotificationScopes
  message: string
  onlyCloseOnce?: boolean
  showSoftAndHardCloseOptions?: boolean
  data?: Record<string, string>
  cta?: {
    label: string
    isUpgradePath: boolean
    action: () => void
  }
}

export interface SuccessNotification {
  type: NotificationTypes.SUCCESS
  id: string
  scope: NotificationScopes
  message: string
  onlyCloseOnce: boolean
  showSoftAndHardCloseOptions: boolean
  data: Record<string, string>
}

export type Notification =
  | ErrorNotification
  | InfoNotification
  | SuccessNotification

type DefaultedNotificationInput<T extends Notification> = Omit<
  T,
  'id' | 'onlyCloseOnce' | 'showSoftAndHardCloseOptions' | 'data'
> &
  Partial<Pick<T, 'onlyCloseOnce' | 'showSoftAndHardCloseOptions' | 'data'>>

type NewInfoNotificationInput = Omit<
  DefaultedNotificationInput<InfoNotification>,
  'type'
> & { type?: NotificationTypes.INFO }
type NewErrorNotificationFactoryInput = Omit<
  DefaultedNotificationInput<ErrorNotification>,
  'type' | 'message'
> & { message?: string }
type NewSuccessNotificationFactoryInput = Omit<
  DefaultedNotificationInput<SuccessNotification>,
  'type' | 'message'
> & { message?: string }
type NewInfoNotificationFactoryInput = Omit<
  DefaultedNotificationInput<InfoNotification>,
  'type' | 'message'
> & { message?: string }

export type NewNotificationInput =
  | DefaultedNotificationInput<ErrorNotification>
  | NewInfoNotificationInput
  | DefaultedNotificationInput<SuccessNotification>

export const Notification = {
  new: (input: NewNotificationInput): Notification => {
    if (input.type === NotificationTypes.ERROR) {
      const {
        scope,
        errorCode,
        message,
        onlyCloseOnce = false,
        showSoftAndHardCloseOptions = false,
        data = {},
      } = input
      return {
        id: uuid(),
        type: input.type,
        scope,
        errorCode,
        message,
        onlyCloseOnce,
        showSoftAndHardCloseOptions,
        data,
      }
    }

    if (input.type === NotificationTypes.SUCCESS) {
      const {
        scope,
        message,
        onlyCloseOnce = false,
        showSoftAndHardCloseOptions = false,
        data = {},
      } = input
      const notification = {
        id: uuid(),
        type: input.type,
        scope,
        errorCode: undefined,
        message,
        onlyCloseOnce,
        showSoftAndHardCloseOptions,
        data,
      }
      return notification
    }

    const {
      scope,
      message,
      onlyCloseOnce = false,
      showSoftAndHardCloseOptions = false,
      data = {},
    } = input
    const notification: InfoNotification & { errorCode: undefined } = {
      id: uuid(),
      type: NotificationTypes.INFO,
      scope,
      errorCode: undefined,
      message,
      onlyCloseOnce,
      showSoftAndHardCloseOptions,
      data,
      ...(input.cta ? { cta: input.cta } : {}),
    }
    return notification
  },
  newFileError: ({ message }: { message: string }): Notification => {
    return Notification.new({
      type: NotificationTypes.ERROR,
      scope: NotificationScopes.FILE_UPLOAD,
      errorCode: 1200,
      message,
    })
  },
  getNewErrorNotification: (
    id: string,
    {
      scope,
      errorCode,
      message = '',
      onlyCloseOnce = false,
      showSoftAndHardCloseOptions = false,
      data = {},
    }: NewErrorNotificationFactoryInput,
  ): ErrorNotification => ({
    type: NotificationTypes.ERROR,
    id,
    scope,
    errorCode,
    message,
    onlyCloseOnce,
    showSoftAndHardCloseOptions,
    data,
  }),

  getNewSuccessNotification: (
    id: string,
    {
      scope,
      message = '',
      onlyCloseOnce = false,
      showSoftAndHardCloseOptions = false,
      data = {},
    }: NewSuccessNotificationFactoryInput,
  ): SuccessNotification => ({
    type: NotificationTypes.SUCCESS,
    id,
    scope,
    message,
    onlyCloseOnce,
    showSoftAndHardCloseOptions,
    data,
  }),

  getNewInfoNotification: (
    id: string,
    {
      scope,
      message = '',
      onlyCloseOnce = false,
      showSoftAndHardCloseOptions = false,
      data = {},
      cta,
    }: NewInfoNotificationFactoryInput,
  ): InfoNotification => ({
    type: NotificationTypes.INFO,
    id,
    scope,
    message,
    onlyCloseOnce,
    showSoftAndHardCloseOptions,
    data,
    cta,
  }),
}
